import {
  PRINTER_SECTION_CLASSNAME,
  PRINTER_ORIENTATION_PORTRAIT_CLASSNAME,
  PRINTER_ORIENTATION_LANDSCAPE_CLASSNAME,
  PRINTER_NATIVE_PRINT_CLASSNAME,
  MARGIN_MM_BOTTOM,
  MARGIN_MM_LEFT,
  MARGIN_MM_RIGHT,
  MARGIN_MM_TOP,
  PAGES_BY_FORMAT_AND_ORIENTATION,
} from '../../GlobalStyles';
import { TINYMCE_CONTENT_CSS } from '../../GlobalStyles/utils/tinymce-content-css.util';

import { cssNormalizer } from './css-normalizer.util';

// MHO : how much space a real printed page actually has left for content
// once its margins are taken out - every rule below that sizes anything to
// "the printable area" must go through these two, or width/height drift out
// of sync the way they just did (page-break separator fixed, background-size
// width left pointing at the full page instead of this).
const getContentAreaHeightMm = (heightMm: number) =>
  heightMm - MARGIN_MM_TOP - MARGIN_MM_BOTTOM;

const getContentAreaWidthMm = (widthMm: number) =>
  widthMm - MARGIN_MM_LEFT - MARGIN_MM_RIGHT;

const getPagePreviewRules = () =>
  PAGES_BY_FORMAT_AND_ORIENTATION.map(
    ({
      formatClassName,
      orientationClassName,
      widthMm,
      heightMm,
      backgroundUrl,
    }) => {
      // MHO : the background SVG's own natural height doesn't match a real
      // printed page's usable content area (page height minus top+bottom
      // margins) - repeating it at its natural size makes the on-screen
      // "next page starts here" separator line land past where the browser's
      // real print pagination actually breaks the page. background-size
      // forces the correct repeat interval without touching the SVG file.
      const contentAreaHeightMm = getContentAreaHeightMm(heightMm);
      const contentAreaWidthMm = getContentAreaWidthMm(widthMm);

      // MHO : the box is sized to the *physical page* (widthMm x heightMm),
      // not the page plus its margins - the white border below (see the
      // .socna-printer-section rule) is drawn INSIDE that box (box-sizing:
      // border-box), so it insets the content area by the margins instead of
      // adding to the page size. Sizing this to widthMm + 2*margin used to
      // make the on-screen text column ~23% wider than the real printable
      // area, so content wrapped onto fewer, longer lines on screen than it
      // does once actually printed - the editor could show 1 page-break line
      // while Ctrl+P produced 3 pages.
      return `
  .${PRINTER_SECTION_CLASSNAME}.${formatClassName}.${orientationClassName} {
    min-height: ${heightMm.toFixed(2)}mm;
    max-width: ${widthMm.toFixed(2)}mm;
    width: ${widthMm.toFixed(2)}mm;
    min-width: ${widthMm.toFixed(2)}mm;
    background: url('${backgroundUrl}') top 0 left 0 repeat;
    background-size: ${contentAreaWidthMm.toFixed(2)}mm ${contentAreaHeightMm.toFixed(2)}mm;
    background-color: white;
  }
`;
    },
  ).join('');

// MHO : caps an image's height in landscape pages to the printable content
// area, so it can't be taller than what actually fits on one physical page.
// Landscape-only because a landscape page is much shorter than it is wide,
// so an ordinary image constrained by the section's width is far more
// likely to end up taller than the page than in portrait.
const getLandscapeImageHeightRules = () =>
  PAGES_BY_FORMAT_AND_ORIENTATION.filter(
    ({ orientationClassName }) =>
      orientationClassName === PRINTER_ORIENTATION_LANDSCAPE_CLASSNAME,
  )
    .map(
      ({ formatClassName, orientationClassName, heightMm }) => `
  .${PRINTER_SECTION_CLASSNAME}.${formatClassName}.${orientationClassName} img {
    max-height: ${getContentAreaHeightMm(heightMm).toFixed(2)}mm;
    width: auto;
  }
`,
    )
    .join('');

// MHO : one named @page per format/orientation combo, since a single unnamed
// @page can't vary its size depending on which section is being printed.
// The `page` property below binds each .socna-printer-section variant to its
// matching @page rule so the browser's native print (TinyMCE's Ctrl+P) uses
// the right physical paper size instead of falling back to the printer's default.
// Scoped to PRINTER_NATIVE_PRINT_CLASSNAME only: the backend PDF pipeline
// (Puppeteer) renders the exact same markup/CSS but always ignores @page CSS,
// forcing its own paper size and zero margins at the protocol level - so these
// rules must not affect its (currently correct, full-bleed) output.
const getPrintPageSizeRules = () =>
  PAGES_BY_FORMAT_AND_ORIENTATION.map(
    ({ formatClassName, orientationClassName, widthMm, heightMm }) => {
      const pageName = `${formatClassName}-${orientationClassName}`;
      // MHO : the section itself must only span the printable area (page size
      // minus @page margins), otherwise its content overflows past the right
      // margin of the physical page during print.
      const contentWidthMm = getContentAreaWidthMm(widthMm);

      return `
  @page ${pageName} {
    size: ${widthMm.toFixed(2)}mm ${heightMm.toFixed(2)}mm;
  }

  .${PRINTER_SECTION_CLASSNAME}.${PRINTER_NATIVE_PRINT_CLASSNAME}.${formatClassName}.${orientationClassName} {
    page: ${pageName};
    max-width: ${contentWidthMm.toFixed(2)}mm;
    width: ${contentWidthMm.toFixed(2)}mm;
    min-width: ${contentWidthMm.toFixed(2)}mm;
  }
`;
    },
  ).join('');

export const getEditorPageStyles = (matches: boolean) => `
${cssNormalizer}

@media print {
  @page {
    margin-top: ${MARGIN_MM_TOP.toFixed(2)}mm;
    margin-right: ${MARGIN_MM_RIGHT.toFixed(2)}mm;
    margin-bottom: ${MARGIN_MM_BOTTOM.toFixed(2)}mm;
    margin-left: ${MARGIN_MM_LEFT.toFixed(2)}mm;
  }

  .${PRINTER_SECTION_CLASSNAME} table,
  .${PRINTER_SECTION_CLASSNAME} div,
  .${PRINTER_SECTION_CLASSNAME} p {
    max-width: 100%;
  }

  .${PRINTER_SECTION_CLASSNAME}.${PRINTER_ORIENTATION_PORTRAIT_CLASSNAME} img {
    max-width: 100%;
    height: auto;
  }

${getPrintPageSizeRules()}
${getLandscapeImageHeightRules()}
}

@media not print {
  html {
    min-height: 100%;
    background: #f4f4f4;
    ${matches ? `padding-top: ${MARGIN_MM_TOP.toFixed(2)}mm;` : 'padding-top: 0;'}
  }

  .${PRINTER_SECTION_CLASSNAME} {
    border-top: solid white ${MARGIN_MM_TOP.toFixed(2)}mm;
    border-right: solid white ${MARGIN_MM_RIGHT.toFixed(2)}mm;
    border-bottom: solid white ${MARGIN_MM_BOTTOM.toFixed(2)}mm;
    border-left: solid white ${MARGIN_MM_LEFT.toFixed(2)}mm;
    box-shadow: 0 0 4px rgba(0,0,0,.15);
    margin: auto;
    ${
      matches
        ? `margin-bottom: ${MARGIN_MM_BOTTOM.toFixed(2)}mm;`
        : 'margin-bottom: 0;'
    }
  }
${getPagePreviewRules()}
}
`;

export const getPrinterContentTypographyStyles = () => `
@scope (.${PRINTER_SECTION_CLASSNAME}) {
${TINYMCE_CONTENT_CSS}
}
`;
